import './scanStatusCompact.less'
import {
  resetWidget,
  fetchScanStatusWidget,
  ScanStatusWidget,
  ScanStatusParams,
  ACTION_SCAN_STATUS_WIDGET
} from './scanStatusSlice'
import { RootState } from '../../rootReducer'
import { ScanStatus } from '../../interfaces'
import { getShowLoader } from '../../reducers/requestReducer'
import useUrlContext from '../../hooks/useUrlContext'
import { getUpdatedUrl } from '../../utils/urlUtil'
import { DATA_SOURCE_ID, DATA_SOURCE_TYPE, DATA_SOURCE_TYPES, URL_TABLES } from '../../constants'
import StatusLabel from '../dataSources/components/StatusWidget/statusLabel'
import {
  getUniqueWidgetIndex,
  STATUS_SCAN_COLORS,
  UNIQUE_WIDGET_INDEX
} from '../dashboards/mainDashboardv2/constants'
import LoaderView from '../../components/Loader/loader'
import { getDataSourceBasePath } from '../../utils/dataSourceUtil'
import { Info as SvgInfo } from 'phosphor-react'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import React, { useEffect } from 'react'
import { Tooltip, Typography } from '@lightbeamai/design-system'
import { Link } from 'react-router-dom'

export const mockScanStatusWidgetData = {
  widgetData: {
    status: ScanStatus.registering,
    scannedCount: 101,
    pendingCount: 2
  },
  messageCount: 15
}

interface OwnProps {
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDashboardId?: string
  datasourceType: DATA_SOURCE_TYPES
  widgetDatasourceType?: DATA_SOURCE_TYPES
}

interface StateProps {
  loader: boolean
  widgetData?: ScanStatusWidget
  messageCount: number
}

interface DispatchProps {
  fetchScanStatusWidget: (params: ScanStatusParams & { [UNIQUE_WIDGET_INDEX]?: string }) => void
  resetWidget: () => void
}

type ScanStatusCompactComponentProps = OwnProps & StateProps & DispatchProps

const StatusWidgetCompact = ({
  widgetData,
  widgetDatasourceId,
  widgetDashboardId,
  datasourceType,
  widgetDatasourceType,
  resetWidget,
  loader,
  fetchScanStatusWidget
}: ScanStatusCompactComponentProps) => {
  const intl = useIntl()
  const context = useUrlContext()
  const datasourceId = widgetDatasourceId || context.contextFilters[DATA_SOURCE_ID]

  const { status = ScanStatus.registering, scannedCount = 0, pendingCount = 0 } = widgetData || {}
  useEffect(() => {
    if (datasourceId && datasourceType)
      fetchScanStatusWidget({
        datasourceId,
        datasourceType,
        [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
      })
    return () => resetWidget()
  }, [datasourceId, datasourceType, fetchScanStatusWidget, resetWidget])

  const pathBaseString = getDataSourceBasePath(widgetDatasourceType, datasourceId, context.pathname)

  const scannedLink = getUpdatedUrl({
    pageName: URL_TABLES,
    pathName: pathBaseString + URL_TABLES,
    queryParams: {
      ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
      ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
    }
  })
  const pendingLink = getUpdatedUrl({
    pageName: URL_TABLES,
    pathName: pathBaseString + URL_TABLES,
    queryParams: {
      ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
      ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
    }
  })

  const isTablesScanned =
    datasourceType === DATA_SOURCE_TYPES.dynamoDb ||
    datasourceType === DATA_SOURCE_TYPES.mongoDb ||
    datasourceType === DATA_SOURCE_TYPES.cosmosMongoDb ||
    datasourceType === DATA_SOURCE_TYPES.cosmosNoSql

  return (
    <div className={`status-widget-structured-compact`} data-test-id="scanstatus-status-widget">
      <div className="widget-status-info">
        <header>
          <Typography variant="baseMinus2" color="custom" type="medium" as="span">
            {intl.formatMessage({ id: 'scanStatus.title' })}
          </Typography>
          <StatusLabel status={status} dataSourceType={datasourceType} />
        </header>

        <div className="summary-count-container">
          {/* Scanned */}
          <Link to={scannedLink}>
            <div className="summary-count">
              <div
                className="status-line"
                style={{
                  backgroundColor:
                    (STATUS_SCAN_COLORS && STATUS_SCAN_COLORS.length > 0
                      ? STATUS_SCAN_COLORS[0]
                      : undefined) ?? '#001529'
                }}
              ></div>
              <Typography variant="baseMinus2" className="font-800" as="span">
                {intl.formatNumber(scannedCount, { notation: 'compact' })}
              </Typography>
              <Typography variant="baseMinus2" className="truncate" as="span">
                {intl.formatMessage({ id: 'scanStatus.scanned' })}&nbsp;
              </Typography>

              <Tooltip
                title={intl.formatMessage({
                  id: isTablesScanned ? 'scanStatus.scannedPopup.dynamo' : 'scanStatus.scannedPopup'
                })}
              >
                <SvgInfo className="summary-count-tooltip" />
              </Tooltip>
            </div>
          </Link>

          {/* Pending */}
          <Link to={pendingLink}>
            <div className="summary-count">
              <div
                className="status-line"
                style={{
                  backgroundColor:
                    (STATUS_SCAN_COLORS && STATUS_SCAN_COLORS.length > 1
                      ? STATUS_SCAN_COLORS[1]
                      : undefined) ?? '#001529'
                }}
              ></div>
              <Typography variant="baseMinus2" className="font-800" as="span">
                {intl.formatNumber(pendingCount, { notation: 'compact' })}
              </Typography>
              <Typography variant="baseMinus2" className="truncate" as="span">
                {intl.formatMessage({ id: 'scanStatus.pending' })}
              </Typography>
            </div>
          </Link>
        </div>
      </div>
      <LoaderView showLoader={loader} size="small" />
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widgetData: ownProps?.useMockData
    ? mockScanStatusWidgetData.widgetData
    : state.scanStatus.widgetDataScanStatus?.[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ],
  messageCount: ownProps?.useMockData
    ? mockScanStatusWidgetData.messageCount
    : state.summary.slack?.messagesCount || 0,
  loader: getShowLoader(ACTION_SCAN_STATUS_WIDGET)
})

const mapDispatchToProps: DispatchProps = {
  fetchScanStatusWidget,
  resetWidget
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(StatusWidgetCompact)
